import React, { forwardRef } from "react";
import styled from "styled-components";
import parse from "html-react-parser";
import IEditableProps from "../../../utilities/IEditableProps";
import IStyledProps from "../../../utilities/IStyledProps";
import classNames from "classnames";
import { getImagePath } from "../../../utilities/path";
import { AutomationTestIdFeature, AutomationTestIdElementType, createDataTestId } from "../../../utilities/automation";
import { usePageProps } from '../../../context/PagePropContext';
import Picture from "../../Picture";

export interface IHeroGridProps extends IEditableProps, IStyledProps {
    mainSrc: string;
    mainAlt: string;
    secondarySrc: string;
    secondaryAlt: string;
    gradientClass: string;
    heroSubTitle;
    heroTitle;
    heroBody;
    heroFooter;
    children;
}

const HeroGrid = forwardRef((props: IHeroGridProps, ref: any) => {
    const { gradientClass, heroSubTitle, heroTitle, heroBody, heroFooter, editorClassObj, className, cmsId } = props;
    const { path } = usePageProps()
    const isMustReads = path.indexOf('must-reads') === 0 || path.indexOf('/must-reads') === 0;
    const mustReadsClassObj = { 'must-reads': isMustReads }

    function getReplacedContent(elements) {
        return React.Children.map(elements, child => {
            if (child.props?.children && typeof child.props?.children === "string") {
                return React.cloneElement(child, child.props, child.props.children.replace(/Ϲ/g, "{").replace(/Ͻ/g, "}"));
            } else {
                return child;
            }
        });
    }

    function parseHTML(html: string) {
        if (!html) return null;
        try {
            return parse(html);
        } catch (e) {
            console.error('HeroGrid widget error:', e);
            return <div className="error-message">Error: Invalid html in "body" or "footer" for HeroGrid widget: {e.message}</div>;
        }
    }

    return (
        <div data-id={cmsId} ref={ref} className={classNames(className, gradientClass, editorClassObj, mustReadsClassObj, "d-flex flex-column")}>
            <div className={"d-flex hero-content container p-0"}>
                <div className={classNames("hero-copy d-flex justify-content-center align-items-center h-100")}>
                    <div className="hero-copy-text container h-100 d-flex flex-column justify-content-center">
                        <h2 className="hero-sub-title text-uppercase mb-2" dangerouslySetInnerHTML={{ __html: heroSubTitle }}></h2>
                        <h1 className="hero-title display-4 mb-4" dangerouslySetInnerHTML={{ __html: heroTitle }}></h1>
                        <div className="hero-body mb-5">
                            {typeof heroBody === 'string' ? parseHTML(heroBody) : getReplacedContent(heroBody)}
                        </div>
                        <div className="hero-footer">
                            {typeof heroFooter === 'string' ? parseHTML(heroFooter) : getReplacedContent(heroFooter)}
                        </div>
                    </div>
                </div>
                <div className={classNames("hero-img-wrap d-flex h-100 p-0")}>
                    <Picture
                        className={"hero-img"}
                        src={getImagePath(props.mainSrc)}
                        alt={props.mainAlt}
                        dataTestId={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.IMAGE, specifier: 'hero-img' })}
                    />
                    {props.children}
                </div>
            </div>
            <div className={"d-none d-lg-flex container hero-img-secondary-wrap w-100 p-0"}>
                <Picture
                    pictureClassName="hero-img-secondary"
                    src={getImagePath(props.secondarySrc)}
                    alt={props.secondaryAlt}
                    dataTestId={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.IMAGE, specifier: 'secondary-img' })}
                />
            </div>
        </div>
    )
});

export default styled(HeroGrid)`

    .hero-content {
        @media (max-width: 990px) {
            align-items: center;
            flex-direction: column;
            max-width: 100%;
        }
    }
    .hero-title {
        @media (max-width: 991px) {
            font-size: 2.22222rem;
        }
    }
    .hero-body {
        font-size: 0.88889rem;
    }
    .hero-copy {
        color: white;
        width: 55%;
        margin: auto;
        @media (max-width: 990px) {
            width: 100%;
        }
    }
    .hero-copy-text {
        padding: 3.56rem;
        @media (max-width: 990px) {
            padding: 2.22rem;
        }
    }
    .hero-img {
        position: absolute;
        height: 100%;
        width: 100%;
        object-fit: cover;
    }
    .hero-img-wrap {
        position: relative;
        text-align: center;
        width: 45%;
        min-height: 29.77rem;
        margin-top: auto;
        @media (max-width: 990px) {
            width: 100%;
            min-height: 14.44rem;
        }
    }
    .hero-img-secondary-wrap {
        @media (max-width: 990px) {
            display: none;
        }
    }
    .hero-img-secondary {
        max-width: 16.61rem;
        margin-left: auto;
        margin-right: 45%;
    }
`;